import { Box, IconButton, InputGroup, NumberDecrementStepper, NumberIncrementStepper, NumberInput, NumberInputField, NumberInputStepper, Spacer, Stack, Tag, Text, Tooltip, useToast } from "@chakra-ui/react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ActionDrawer } from "components/drawer";
import { create, getAll } from "lib/firebase";
import { computeDuration, findCopy, repeatList } from "lib/broadcast";
import { hasNoContent, isCompositeBroadcast } from "lib/playlist";
import { formatSeconds } from "lib/utils";
import moment from "moment";
import { useState } from "react";
import { MdCheck, MdDragIndicator, MdPlaylistAdd, MdPlaylistRemove } from "react-icons/md";
import BroadcastModal from "views/modal/broadcast";
import { BroadcastTypeIcon, NEW_ENTRY_BG, NEW_ENTRY_TITLE, SelectionNumber } from "./common";

// Module-level components (see ContentItems.js): defining them inside
// BroadcastSelection would re-create the whole list on every render.

// The name of a REGULAR broadcast opens its editor in a second drawer, so it can be changed
// while a composite broadcast is being created or edited. `target` is the broadcast that
// is edited: the broadcast itself, or the original of a multiplier copy (a copy follows its
// original, so editing the copy directly would be overwritten). A composite broadcast, or a
// copy whose original is gone, is shown as plain text.
function BroadcastName({ item, target, onSaved }) {
  // A regular broadcast may be empty (no content list at all), so the kind is not read from the list
  const editable = !item.missing && !!target && !isCompositeBroadcast(target) && !target.sourceId
  if (!editable) {
    return <Text fontSize='sm' fontWeight='medium'>{item.name}</Text>
  }

  return (
    <ActionDrawer
      title={'Update broadcast: ' + target.name}
      size={'xxl'}
      trigger={
        <Tooltip label={item.sourceId ? 'Edit the original broadcast' : 'Edit broadcast'}>
          <Text fontSize='sm' fontWeight='medium' color='brand.500' cursor='pointer' _hover={{ textDecoration: 'underline' }}>{item.name}</Text>
        </Tooltip>
      }
      >
      <BroadcastModal broadcast={target} onChange={onSaved} />
    </ActionDrawer>
  )
}

function Title({ item, target, onSaved }) {
  return (
    <>
      <BroadcastTypeIcon composite={isCompositeBroadcast(item)} />
      <BroadcastName item={item} target={target} onSaved={onSaved} />
      {!item.missing && hasNoContent(item) && <Tag fontSize='xs' variant='subtle' colorScheme='orange' title='This broadcast has no content yet. It plays nothing and does not disturb the others.'>empty</Tag>}
      <Text fontSize='sm' color='gray'>{item.updated_at ? moment(item.updated_at).format('LLL') : ""}</Text>
      <Tag fontSize='xs' variant='subtle' colorScheme='cyan'>{formatSeconds(item.duration)}</Tag>
    </>
  )
}

export function AvailableBroadcastItem({ item, count, onAdd, onSaved }) {
  return (
    <Box bg={count > 0 ? 'green.50' : 'white'} w='100%' borderWidth='1px' borderRadius='lg' p={4}>
      <Stack direction='row' alignItems='center' wrap='wrap' gap={2} spacing={0}>
        <Title item={item} target={item} onSaved={onSaved} />
        <Spacer />
        {count > 0 && <Tag fontSize='xs' variant='subtle' colorScheme='green'>x {count}</Tag>}
        <IconButton aria-label='Add' size='xs' variant='outline' icon={<MdPlaylistAdd />} colorScheme='brand' onClick={() => onAdd(item.id)} />
      </Stack>
    </Box>
  )
}

// `source` is the original broadcast when `item` is itself a multiplier broadcast, so a
// new multiplier is always calculated from the original and never compounds.
export function SelectedBroadcastItem({ item, source, position, onRemove, onReplace, onRefresh, onSaved }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.sortKey });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.7 : 1, position: 'relative', zIndex: isDragging ? 1 : 0 };

  const toast = useToast()
  const currentMultiplier = item.multiplier || 1
  // The field keeps the text being typed (it can be empty while the user types a new
  // number); the multiplier itself is always a whole number, 1 or more.
  const [multiplierText, setMultiplierText] = useState(String(currentMultiplier))
  const multiplier = Math.max(1, Math.floor(Number(multiplierText)) || 1)
  const [busy, setBusy] = useState(false)

  // Puts the broadcast with the chosen multiplier at this position of the playlist:
  // - 1 means the original broadcast itself,
  // - otherwise the existing copy with that multiplier, or a new one when there is none
  //   (so the same multiplier is never created twice).
  // The copy is always built from the original, never from another copy.
  const applyMultiplier = async () => {
    setBusy(true)
    try {
      const base = source || item
      if (isCompositeBroadcast(base) || base.sourceId) {
        throw new Error('A multiplier can only be set on a regular broadcast.')
      }
      if (multiplier > 1 && hasNoContent(base)) {
        throw new Error('This broadcast is empty, there is nothing to repeat.')
      }

      let target = base
      if (multiplier > 1) {
        // Fresh data from the database, so a copy made a moment ago is found too
        const [allBroadcasts, contentItems] = await Promise.all([
          getAll('broadcast', base.clientId),
          getAll('content', base.clientId),
        ])
        target = findCopy(allBroadcasts, base.id, multiplier)
        if (!target) {
          const contentList = repeatList(base.contentList, multiplier)
          const payload = {
            ...base,
            sourceId: base.id,
            multiplier: multiplier,
            updated_at: moment().toISOString(),
            contentList,
            duration: computeDuration({ contentList }, { contentItems }) ?? (base.duration || 0) * multiplier,
          }
          // Fields that only exist for the UI or belong to the original broadcast
          delete payload.id
          delete payload.sortKey
          delete payload.schedule
          delete payload.missing
          target = await create('broadcast', payload)
        }
      }

      await onRefresh()
      onReplace(position - 1, target.id)
      toast({ title: multiplier > 1 ? `Multiplier x${multiplier} applied` : 'Multiplier removed', status: 'success', duration: 3000, isClosable: true })
    } catch (err) {
      toast({ title: 'Error!', description: err.message || err, status: 'error', duration: null, isClosable: true })
    } finally {
      setBusy(false)
    }
  }

  // Any change of the number can be confirmed, including going back to 1
  const changed = multiplier !== currentMultiplier

  return (
    <div ref={setNodeRef} style={style}>
      <Box bg={item.isNew ? NEW_ENTRY_BG : 'white'} title={item.isNew ? NEW_ENTRY_TITLE : undefined} w='100%' borderWidth='1px' borderRadius='lg' p={4}>
        <Stack direction='row' alignItems='center' wrap='wrap' gap={2} spacing={0}>
          <MdDragIndicator {...listeners} {...attributes} style={{ cursor: 'grab', flexShrink: 0 }} />
          <SelectionNumber value={position} />
          {item.missing
            ? <Text fontSize='sm' color='red.500'>Broadcast no longer exists</Text>
            : <Title item={item} target={item.sourceId ? source : item} onSaved={onSaved} />}
          <Spacer />
          {!item.missing && (
            <Tooltip label='Multiplier'>
              <InputGroup size='xs' w={16}>
                <NumberInput step={1} min={1} precision={0} size='xs' value={multiplierText} onChange={setMultiplierText}>
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper color='gray' />
                    <NumberDecrementStepper color='gray' />
                  </NumberInputStepper>
                </NumberInput>
              </InputGroup>
            </Tooltip>
          )}
          {changed && !item.missing && (
            <Tooltip label={multiplier > 1 ? `Apply multiplier x${multiplier}` : 'Back to the original broadcast'}>
              <IconButton aria-label='Apply multiplier' size='xs' variant='outline' icon={<MdCheck />} colorScheme='green' isLoading={busy} onClick={applyMultiplier} />
            </Tooltip>
          )}
          <IconButton aria-label='Remove' size='xs' variant='outline' icon={<MdPlaylistRemove />} colorScheme='red' isDisabled={busy} onClick={() => onRemove(position - 1)} />
        </Stack>
      </Box>
    </div>
  )
}
